/**
 * Show a pop-up to set medical conditions of one subject
 *
 * @param animalId
 *        database ID of the animal for which to set medical conditions
 *
 * @param pupId
 *        database ID of the pup for which to set medical conditions
 *
 * @param tankId
 *        database ID of the tank for which to set medical conditions
 *
 * @param eventTarget: HTMLElement anchor for dialog (position of pop-up).
 *
 * @param reloadCallback
 *        function to call when data has been applied and pop-up is closed,
 *        e.g. to reload a list or detail page to display the new data
 *
 * @param closeCallback
 *        function to call whenever the pop-up is closed, whther data was
 *        applied or not, e.g. to unhighlight a row in the listview table
 */

import * as _ from "lodash";
import * as ko from "knockout";
import {Observable} from "knockout";
import {ObservableArray} from "knockout";
import {PureComputed} from "knockout";
import {dialogStarter} from "../knockout/dialogStarter";
import {FetchExtended} from "../knockout/extensions/fetch";
import {getTranslation} from "../lib/localize";
import {KnockoutPopup} from "../lib/popups";
import {session} from "../lib/pyratSession";
import {notifications} from "../lib/pyratTop";
import {getFormData} from "../lib/utils";
import {getUrl} from "../lib/utils";
import {AjaxResponse} from "../lib/utils";
import template from "./setMedicalConditions.html";

interface MedicalConditionsParams {
    animalId?: number;
    pupId?: number;
    tankId?: number;
    eventTarget?: HTMLElement;
    reloadCallback?: () => void;
    closeCallback?: () => void;
}

interface Seed {
    subject_name: string;
    selected_medical_condition_ids: number[];
    available_medical_conditions: {id: number; name: string}[];
    tank_status?: "open" | "closed" | "exported" | "joined";
}

class SetMedicalConditionsViewModel {

    private readonly dialog: KnockoutPopup;
    private readonly animalId: number;
    private readonly pupId: number;
    private readonly tankId: number;

    public readonly seed: FetchExtended<Observable<AjaxResponse<Seed | undefined>>>;
    public readonly selectedMedicalConditionIds: ObservableArray<number>;
    public readonly availableMedicalConditions: ObservableArray<{id: number; name: string}>;

    public readonly allowEdit: PureComputed<boolean>;
    public readonly canApply: PureComputed<boolean>;
    private readonly applyInProgress: Observable<boolean>;
    private readonly reloadRequired: Observable<boolean>;
    private readonly reloadCallback: () => void;
    private readonly closeCallback: () => void;

    constructor({
        animalId,
        pupId,
        tankId,
        reloadCallback,
        closeCallback,
    }: MedicalConditionsParams, dialog: KnockoutPopup) {

        this.dialog = dialog;
        this.animalId = animalId;
        this.pupId = pupId;
        this.tankId = tankId;
        this.reloadCallback = reloadCallback;
        this.closeCallback = closeCallback;

        this.selectedMedicalConditionIds = ko.observableArray();
        this.availableMedicalConditions = ko.observableArray();

        this.seed = ko.observable().extend({
            fetch: (signal) => {
                const data: {
                        animal_id?: number;
                        pup_id?: number;
                        tank_id?: number;
                    } = {};

                if (animalId) {
                    data.animal_id = animalId;
                }

                if (pupId) {
                    data.pup_id = pupId;
                }

                if (tankId) {
                    data.tank_id = tankId;
                }

                return fetch(getUrl("set_medical_conditions.py", data), {signal});
            },
        });
        this.seed.subscribe((newSeed) => {
            if (newSeed?.success) {
                this.selectedMedicalConditionIds(newSeed.selected_medical_condition_ids);
                this.availableMedicalConditions(newSeed.available_medical_conditions);
            }
        });

        /**
         * Allow to edit medical conditions based on user permissions
         * (=> for tanks: conditions are only editable if tank status is "open")
         */
        this.allowEdit = ko.pureComputed(() => {
            return (!this.animalId || session.userPermissions.animal_set_condition)
                && (!this.pupId || session.userPermissions.animal_set_condition)
                && (!this.tankId || session.userPermissions.tank_update && this.seed()?.tank_status === "open");
        });


        this.dialog.addOnClose(() => {
            if (this.closeCallback) {
                this.closeCallback();
            }

            if (this.reloadCallback && this.reloadRequired()) {
                this.reloadCallback();
            }
        });
        this.seed.subscribe((seed) => {
            const subjectLabel = seed?.success && seed.subject_name || "";
            if (this.allowEdit) {
                this.dialog.setTitle(getTranslation("Set conditions for %s").replace("%s", subjectLabel));
            } else {
                this.dialog.setTitle(getTranslation("View conditions of %s").replace("%s", subjectLabel));
            }
        });
        this.applyInProgress = ko.observable(false);
        this.canApply = ko.pureComputed(() => {
            const seed = this.seed();

            return _.xor(this.selectedMedicalConditionIds(), seed?.success && seed.selected_medical_condition_ids || []).length &&
                    !this.applyInProgress();
        });
        this.reloadRequired = ko.observable(false);

    }

    public submitMedicalConditions = () => {

        this.applyInProgress(true);
        this.reloadRequired(true);

        const tmpForm = getFormData({
            medical_condition_ids: JSON.stringify(this.selectedMedicalConditionIds() || []),
        });
        if (this.animalId) {
            tmpForm.append("animal_id", this.animalId.toString());
        }
        if (this.pupId) {
            tmpForm.append("pup_id", this.pupId.toString());
        }
        if (this.tankId) {
            tmpForm.append("tank_id", this.tankId.toString());
        }

        fetch("set_medical_conditions.py", {
            method: "POST",
            body: tmpForm,
        }).then(response => response.json()).then((response: AjaxResponse<any>) => {
            this.applyInProgress(false);
            if (response.success) {
                this.dialog.close();
                notifications.showNotification(response.message, "success");
            } else {
                notifications.showNotification(response.message, "error");
            }
        }).catch(() => {
            this.applyInProgress(false);
            notifications.showNotification(getTranslation("Action failed. The data could not be saved. Please try again."), "error");
        });
    };
}

export const showSetMedicalConditions = dialogStarter(SetMedicalConditionsViewModel, template, params => ({
    name: "SetMedicalConditions",
    width: 450,
    handle: "right top",
    anchor: params.eventTarget,
    escalate: false,
    closeOthers: true,
    cssARequire: [":table.css"],
}));
